// src/pages/AboutPage.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  // BellIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import LoadingSpinner from '../components/LoadingSpinner';

const AboutPage = () => {
  const location = useLocation();
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');

  // URLのクエリパラメータからタブを設定
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['about', 'howto', 'announcements'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // お知らせ情報を取得
  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (activeTab === 'announcements') {
        try {
          setIsLoading(true);
          setError(null);
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/announcements`);
          setAnnouncements(response.data);
          setIsLoading(false);
        } catch (error) {
          console.error('お知らせ取得エラー:', error);
          setError('お知らせの取得に失敗しました');
          setIsLoading(false);
        }
      }
    };

    fetchAnnouncements();
  }, [activeTab]);

  // タブを変更したときの処理
  const handleTabChange = tab => {
    setActiveTab(tab);

    // URLを更新（履歴に追加）
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);

    // お知らせタブに切り替えたときのみデータ取得
    if (tab === 'announcements' && announcements.length === 0) {
      setIsLoading(true);
    }
  };

  // 新しいお知らせをチェック
  const hasNewAnnouncements = () => {
    if (!announcements.length) return false;

    const lastVisit = localStorage.getItem('lastAnnouncementsVisit');
    if (!lastVisit) return true;

    const lastVisitDate = new Date(lastVisit);
    return announcements.some(announcement => new Date(announcement.publishedAt) > lastVisitDate);
  };

  // お知らせを見たときのタイムスタンプを保存
  useEffect(() => {
    if (activeTab === 'announcements' && !isLoading) {
      localStorage.setItem('lastAnnouncementsVisit', new Date().toISOString());
    }
  }, [activeTab, isLoading]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* タブナビゲーション */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => handleTabChange('about')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'about'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex justify-center items-center">
              <InformationCircleIcon className="h-5 w-5 mr-1" />
              システム概要
            </div>
          </button>

          <button
            onClick={() => handleTabChange('howto')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'howto'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex justify-center items-center">
              <QuestionMarkCircleIcon className="h-5 w-5 mr-1" />
              使い方
            </div>
          </button>

          <button
            onClick={() => handleTabChange('announcements')}
            className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
              activeTab === 'announcements'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex justify-center items-center">
              <LightBulbIcon className="h-5 w-5 mr-1" />
              お知らせ
              {hasNewAnnouncements() && activeTab !== 'announcements' && (
                <span className="ml-1 inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </div>
          </button>
        </div>

        <div className="p-6">
          {/* システム概要タブ */}
          {activeTab === 'about' && (
            <div>
              <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                YukichiCoin 投票システムについて
              </h1>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                  概要
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  YukichiCoin投票システムは、OASYSブロックチェーン上のyukichi.funプラットフォームで発行したコインを使用して、
                  コミュニティが透明で公正な投票を行うための分散型意思決定プラットフォームです。
                </p>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  コインを発行したアドレスが提案を作成し、コミュニティメンバーは保有コインを活用して投票できます。
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                  主な特徴
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    <span className="font-medium">多様な投票方式:</span>{' '}
                    「1アドレス1票」または「コイン量比例」の投票方式を選択可能
                  </li>
                  <li>
                    <span className="font-medium">未Claim対応:</span>{' '}
                    未Claimのコインも投票権に含めるオプション
                  </li>
                  <li>
                    <span className="font-medium">リアルタイム集計:</span>{' '}
                    投票結果のリアルタイム表示と透明な集計
                  </li>
                  <li>
                    <span className="font-medium">安全なウォレット認証:</span>{' '}
                    Web3ウォレットによる署名認証で安全な投票を実現
                  </li>
                  <li>
                    <span className="font-medium">コメント機能:</span>{' '}
                    提案に対してディスカッションが可能
                  </li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                  対応コイン
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  yukichi.funプラットフォームで発行し
                  <span className="font-bold text-red-600 dark:text-red-400">上場した</span>
                  コインに対応しています。 詳細なコイン一覧は
                  <Link
                    to="/tokens"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    こちら
                  </Link>
                  から確認できます。
                </p>
              </div>

              {/* <div>
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">今後の開発予定</h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>複数チェーン対応: 他のブロックチェーンへの拡張</li>
                  <li>高度な投票方式: 複数選択や投票の委任機能</li>
                  <li>実行結果の自動化: 投票結果に基づくスマートコントラクト実行</li>
                  <li>通知システムの強化: メール通知やモバイル対応</li>
                  <li>ダッシュボード機能: 詳細な分析ツールとグラフ表示</li>
                </ul>
              </div> */}
            </div>
          )}

          {/* 使い方タブ */}
          {activeTab === 'howto' && (
            <div>
              <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                システムの使い方
              </h1>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                  <div className="flex items-center">
                    <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-2">
                      1
                    </span>
                    ウォレットの接続
                  </div>
                </h2>
                <div className="ml-8 text-gray-700 dark:text-gray-300">
                  <p className="mb-2">
                    システムを利用するには、まずMetaMaskなどのWeb3ウォレットを接続する必要があります。
                  </p>
                  <ol className="list-decimal pl-6 space-y-1">
                    <li>画面上部の「ウォレット接続」ボタンをクリック</li>
                    <li>利用するウォレットを選択し、接続を許可</li>
                    {/* <li>
                      OASYSネットワークに接続していない場合は、ネットワークの切り替えを行ってください
                    </li> */}
                  </ol>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                  <div className="flex items-center">
                    <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-2">
                      2
                    </span>
                    提案の作成
                  </div>
                </h2>
                <div className="ml-8 text-gray-700 dark:text-gray-300">
                  <p className="mb-2">
                    提案を作成するには、以下のいずれかの条件を満たす必要があります：
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mb-3">
                    <li>yukichi.funでコインを発行したアドレスであること</li>
                    <li>10,000 LIDERU以上のコインを保有していること</li>
                    <li>管理者として登録されていること</li>
                  </ul>

                  <p className="mb-2">提案作成の手順：</p>
                  <ol className="list-decimal pl-6 space-y-1">
                    <li>「提案」ページの「新しい提案」ボタンをクリック</li>
                    <li>提案のタイトルと詳細な説明を入力</li>
                    <li>投票権を付与するコインを選択</li>
                    <li>投票期間（開始・終了日時）を設定</li>
                    <li>投票方式（「1アドレス1票」または「コイン量比例」）を選択</li>
                    <li>未Claimコインを投票権に含めるかどうかを選択</li>
                    <li>投票の選択肢を2つ以上設定</li>
                    <li>「提案を作成」ボタンをクリックし、ウォレットで署名</li>
                  </ol>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">
                  <div className="flex items-center">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2">
                      3
                    </span>
                    投票の参加
                  </div>
                </h2>
                <div className="ml-8">
                  <p className="mb-2">
                    投票に参加するには、提案で指定されたコインを投票開始時点で保有している必要があります。
                  </p>
                  <ol className="list-decimal pl-6 space-y-1">
                    <li>「提案」ページから投票したい提案を選択</li>
                    <li>提案詳細ページの投票セクションで選択肢を選択</li>
                    <li>「投票する」ボタンをクリックし、ウォレットで署名</li>
                    <li>投票期間中であれば、いつでも再投票が可能です</li>
                    {/* <li>投票後、リアルタイムで更新される結果グラフを確認できます</li> */}
                  </ol>

                  {/* <div className="mt-2 bg-blue-50 p-3 rounded-md">
                    <p className="text-blue-800 text-sm">
                      <strong>新機能:</strong>{' '}
                      投票結果セクションの「リアルタイム更新」を有効にすることで、
                      他のユーザーの投票状況をリアルタイムで確認できるようになりました！
                    </p>
                  </div> */}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">
                  <div className="flex items-center">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2">
                      4
                    </span>
                    コメント機能
                  </div>
                </h2>
                <div className="ml-8">
                  <p className="mb-2">
                    各提案にはコメント機能があり、提案についての議論が可能です。
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>提案詳細ページの「コメント」タブを選択</li>
                    <li>コメントを入力して投稿</li>
                    <li>他のユーザーのコメントに返信することも可能</li>
                    <li>コメントに対して、賛成/反対の投票も行えます</li>
                    <li>自分のコメントは編集・削除が可能です</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4">
                  <div className="flex items-center">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2">
                      5
                    </span>
                    プロフィールの確認
                  </div>
                </h2>
                <div className="ml-8">
                  <p className="mb-2">
                    自分のプロフィールページでは、作成した提案や投票履歴を確認できます。
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>画面上部の「プロフィール」をクリック</li>
                    <li>自分の提案履歴を確認</li>
                    <li>自分の投票履歴を確認</li>
                    <li>発行コインの一覧を確認</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* お知らせタブ */}
          {activeTab === 'announcements' && (
            <div>
              <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">お知らせ</h1>

              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <LoadingSpinner />
                </div>
              ) : error ? (
                <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              ) : announcements.length > 0 ? (
                <div className="space-y-6">
                  {announcements.map(announcement => (
                    <div
                      key={announcement.id}
                      className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-b-0"
                    >
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-1" />
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {announcement.title}
                          </h2>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {format(new Date(announcement.publishedAt), 'yyyy年MM月dd日', {
                              locale: ja,
                            })}
                          </p>
                          <div className="prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-gray-300">
                            <div dangerouslySetInnerHTML={{ __html: announcement.content }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                  <LightBulbIcon className="h-12 w-12 text-yellow-500 dark:text-yellow-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-300">現在、お知らせはありません</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
